import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Camera, 
  Shield, 
  Eye, 
  Brain,
  Wifi,
  Cloud,
  AlertTriangle,
  Check,
  ArrowRight,
  ArrowLeft,
  Truck,
  Video,
  Zap,
  Lock,
  FileText,
  Loader2,
  CreditCard,
  Package,
  Clock,
  Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPageView, trackFormSubmit } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Camera features
const cameraFeatures = [
  {
    icon: Eye,
    title: 'Dual HD Cameras',
    description: 'Road-facing and cabin-facing cameras capture every angle in crystal clear 1080p.'
  },
  {
    icon: Brain,
    title: 'AI Safety Monitoring',
    description: 'Real-time detection of distracted driving, drowsiness, phone use, and unsafe behaviors.'
  },
  {
    icon: Cloud,
    title: 'Cloud Storage',
    description: 'Automatic upload to secure cloud. Access footage from anywhere, anytime.'
  },
  {
    icon: Wifi,
    title: '4G LTE Connected',
    description: 'Always connected. Live streaming and instant incident alerts to your dashboard.'
  },
  {
    icon: Shield,
    title: 'Incident Protection',
    description: 'Automatic incident detection saves crucial footage. Protect against false claims.'
  },
  {
    icon: Lock,
    title: 'HIPAA Compliant',
    description: 'Patient privacy protected. Audio can be disabled during medical transport.'
  }
];

// Why cameras matter
const whyCameras = [
  {
    stat: '40%',
    label: 'Reduction in incidents',
    description: 'Fleets with dash cams see significantly fewer accidents and unsafe driving events.'
  },
  {
    stat: '70%',
    label: 'Faster claim resolution',
    description: 'Video evidence speeds up insurance claims and protects against fraud.'
  },
  {
    stat: '100%',
    label: 'Broker compliance',
    description: 'Many broker contracts now require camera systems. Be ready for any opportunity.'
  }
];

export const CamerasPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    fleet_size: '',
    camera_quantity: 1,
    current_camera_system: '',
    message: '',
    smsConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [depositAmount, setDepositAmount] = useState(100);

  useEffect(() => {
    trackPageView('/cameras', 'nemt_vision');
  }, []);

  // Update deposit amount when quantity changes
  useEffect(() => {
    setDepositAmount(formData.camera_quantity * 100);
  }, [formData.camera_quantity]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'camera_quantity' ? parseInt(value) || 1 : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.email || !formData.camera_quantity) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit order to backend
      const response = await axios.post(`${API}/camera-orders`, {
        ...formData,
        deposit_amount: depositAmount,
        status: 'pending_payment'
      });

      // Track form submission
      trackFormSubmit('camera_order', 'nemt_vision', { 
        quantity: formData.camera_quantity,
        deposit: depositAmount 
      });

      // If Stripe is configured, redirect to checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        // Stripe not configured yet - show success
        setIsSubmitted(true);
        toast.success('Order submitted! We\'ll contact you when payment processing is ready.');
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-medex-bg flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-neon-green" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-4">
            Order Received!
          </h2>
          <p className="text-slate-400 mb-6">
            Thank you for your interest in NEMT Vision cameras. We've received your pre-order for 
            <span className="text-neon-cyan font-semibold"> {formData.camera_quantity} camera{formData.camera_quantity > 1 ? 's' : ''}</span>.
          </p>
          <div className="glass p-4 rounded-lg mb-6 text-left">
            <h4 className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-2">What Happens Next?</h4>
            <ol className="text-sm text-slate-300 space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-neon-cyan">1.</span>
                <span>We'll contact you to confirm your order details</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-neon-cyan">2.</span>
                <span>Process your ${depositAmount} deposit (${100}/camera)</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-neon-cyan">3.</span>
                <span>Ship cameras with installation instructions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-neon-cyan">4.</span>
                <span>Provide onboarding and platform access</span>
              </li>
            </ol>
          </div>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Return to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-medex-bg">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-cyan/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 mb-6">
                <Camera className="w-4 h-4 text-neon-cyan" />
                <span className="text-sm text-neon-cyan font-medium">NEMT Vision</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                AI-Powered <span className="text-neon-cyan">Dash Cams</span> for NEMT
              </h1>
              
              <p className="text-xl text-slate-400 mb-8">
                Protect your fleet, your drivers, and your business with intelligent camera systems 
                designed specifically for medical transportation.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <a href="#order" className="btn-primary inline-flex items-center space-x-2">
                  <span>Pre-Order Now</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#features" className="btn-secondary">
                  View Features
                </a>
              </div>

              {/* Pricing Coming Soon Badge */}
              <div className="inline-flex items-center space-x-3 px-4 py-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <Clock className="w-5 h-5 text-yellow-400" />
                <div>
                  <span className="text-yellow-400 font-semibold">Full Pricing Coming Soon</span>
                  <p className="text-sm text-slate-400">Reserve yours with $100 deposit per camera</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              {/* Camera Images */}
              <div className="relative">
                <div className="glass-card p-4 border-neon-cyan/30 overflow-hidden">
                  <img 
                    src="/images/nemt-vision-camera-1.png" 
                    alt="NEMT Vision AI Dash Camera" 
                    className="w-full h-auto rounded-lg"
                  />
                  <div className="absolute top-6 right-6 px-3 py-1 bg-neon-cyan/90 text-black text-xs font-bold rounded-full">
                    HD 1080p
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 glass-card p-4 border-neon-green/30">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-neon-green/20 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-neon-green" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">AI Powered</div>
                      <div className="text-xs text-slate-400">Real-time analysis</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {whyCameras.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-heading font-bold text-neon-cyan mb-2">{item.stat}</div>
                <div className="text-lg font-semibold text-white mb-2">{item.label}</div>
                <p className="text-sm text-slate-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Built for <span className="text-neon-cyan">NEMT</span> Operations
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Not just any dash cam - NEMT Vision is designed specifically for medical transportation 
              with features that matter to your business.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cameraFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 hover:border-neon-cyan/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-neon-cyan/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-neon-cyan" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Second Camera Image */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="glass-card p-4 border-neon-cyan/20">
                <img 
                  src="/images/nemt-vision-camera-2.png" 
                  alt="NEMT Vision Camera Detail" 
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-heading font-bold text-white mb-4">
                Professional Grade Hardware
              </h3>
              <ul className="space-y-4">
                {[
                  'Dual-lens system: road + cabin view',
                  'Night vision with IR illumination',
                  'Wide-angle 170° field of view',
                  'Built-in GPS for location tracking',
                  'G-sensor for automatic incident capture',
                  'Easy installation - plug and play'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-neon-green" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Order Form Section */}
      <section id="order" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Pre-Order <span className="text-neon-cyan">NEMT Vision</span>
            </h2>
            <p className="text-lg text-slate-400">
              Reserve your cameras with a $100 deposit per unit. Full pricing announced soon.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="glass-card p-8"
          >
            {/* Deposit Calculator */}
            <div className="mb-8 p-6 bg-neon-cyan/10 border border-neon-cyan/30 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Package className="w-6 h-6 text-neon-cyan" />
                  <span className="text-white font-semibold">Camera Quantity</span>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, camera_quantity: Math.max(1, prev.camera_quantity - 1) }))}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    name="camera_quantity"
                    value={formData.camera_quantity}
                    onChange={handleChange}
                    min="1"
                    max="100"
                    className="w-16 text-center input-dark"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, camera_quantity: prev.camera_quantity + 1 }))}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-slate-400">Deposit Amount ($100/camera)</span>
                <span className="text-2xl font-heading font-bold text-neon-green">${depositAmount}</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="Your Transport Company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full input-dark"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Fleet Size
                </label>
                <select
                  name="fleet_size"
                  value={formData.fleet_size}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Select fleet size</option>
                  <option value="1-5">1-5 vehicles</option>
                  <option value="6-15">6-15 vehicles</option>
                  <option value="16-30">16-30 vehicles</option>
                  <option value="31-50">31-50 vehicles</option>
                  <option value="50+">50+ vehicles</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Current Camera System
                </label>
                <select
                  name="current_camera_system"
                  value={formData.current_camera_system}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Do you have cameras?</option>
                  <option value="none">No cameras currently</option>
                  <option value="basic">Basic dash cams</option>
                  <option value="fleet_system">Fleet camera system</option>
                  <option value="other">Other system</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Additional Notes
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className="w-full input-dark resize-none"
                placeholder="Any specific requirements or questions?"
              />
            </div>

            {/* SMS Consent Checkbox - SEPARATE OPT-IN */}
            <div className="mb-6 p-4 bg-slate-800/80 rounded-lg border-2 border-neon-cyan/30">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-neon-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                SMS Text Message Consent
              </h4>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="smsConsent"
                  checked={formData.smsConsent || false}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 rounded border-2 border-neon-cyan bg-slate-700 text-neon-green focus:ring-neon-green focus:ring-offset-0"
                  data-testid="sms-consent-checkbox"
                />
                <div className="text-xs text-slate-300 leading-relaxed">
                  <p className="mb-2">
                    <strong className="text-white">I agree to receive SMS text messages from MedexOVA</strong> at the phone number provided above regarding order status, shipping updates, and service notifications.
                  </p>
                  <p className="text-slate-400">
                    Message frequency may vary. Standard message and data rates may apply. Reply <strong className="text-neon-cyan">STOP</strong> to opt out. Reply <strong className="text-neon-cyan">HELP</strong> for assistance. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.
                  </p>
                  <p className="mt-2 text-slate-500">
                    View our <a href="/privacy-policy#sms-consent" target="_blank" className="text-neon-cyan hover:underline">SMS Policy</a> for more details.
                  </p>
                </div>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  <span>Reserve Cameras - ${depositAmount} Deposit</span>
                </>
              )}
            </button>

            <p className="text-xs text-slate-500 text-center mt-4">
              Your deposit secures your place in our shipment queue. Full pricing and 
              payment will be communicated before cameras ship. Deposits are refundable.
            </p>
          </motion.form>
        </div>
      </section>

      {/* Insurance Tie-in */}
      <section className="py-16 bg-gradient-to-r from-neon-cyan/10 via-neon-purple/10 to-neon-cyan/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 border-neon-purple/30 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-neon-purple/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-7 h-7 text-neon-purple" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Bundle with Insurance</h3>
                <p className="text-slate-400 max-w-xl">
                  NEMT Vision integrates with Medex Group Insurance. Some broker contracts require 
                  camera systems - having NEMT Vision positions you for more opportunities and 
                  potential premium discounts.
                </p>
              </div>
            </div>
            <a
              href="/insurance/details"
              className="btn-secondary whitespace-nowrap inline-flex items-center space-x-2"
            >
              <span>Learn About Insurance</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CamerasPage;
